import type {BehaviorSubject, Subject} from "rxjs";
import type {InjectionToken} from "tsyringe";
import type {Linkview} from "../entities/linkview.entity";
import type {LinkTile} from "../entities/link-tile.entity";

/**
 * Persistence contract for Linkview boards + their tile layouts (Anke,
 * 2026-09-04: "Der Service um das Dashboard ins LocalStorage zu speichern,
 * muss auf einem Interface beruhen, so dass wir es gegen eine andere
 * Implementierung austauschen können") — `LocalStorageLinkviewRepository` is
 * the only implementation today; a future backend-backed one just needs
 * registering under `LINKVIEW_REPOSITORY` in `bootstrap.ts` instead.
 *
 * Async from the start (Anke): `items$`/`addTileRequested$` stay RxJS streams
 * (fit synchronous and asynchronous backing alike), but every method that
 * means real I/O for a backend implementation returns a `Promise` — so a
 * later swap changes `bootstrap.ts`'s one registration line, not every call
 * site. `requestAddTile` stays synchronous: it's a pure one-shot signal
 * emission (see `addTileRequested$`'s own doc comment), never I/O.
 */
export abstract class AbstractLinkviewRepository {

    public abstract readonly items$: BehaviorSubject<Linkview[]>;

    /**
     * One-shot signal: "please open the create-tile dialog for THIS
     * Linkview" (the "+ Neue Kachel" button in `o2d-linkview.view.ts`'s
     * toolbar; it lived on the sidebar row until 2026-09-17). The button
     * and the `dashboard-creator` instance it targets are SIBLINGS in the
     * DOM, not parent/child — this Subject is how the click reaches the
     * right (currently mounted) instance. Carries the target `uid`; a listener ignores emissions for
     * any other uid. Deliberately a plain `Subject`, not a `BehaviorSubject`
     * — this is a one-shot ACTION, not state a late subscriber should replay.
     */
    public abstract readonly addTileRequested$: Subject<string>;

    /**
     * One-shot signal: "please save THIS Linkview's board now" (Anke,
     * 2026-09-17: the toolbar's "Speichern" button lives in
     * `o2d-linkview.view.ts`, a SIBLING of the `dashboard-creator` instance
     * it targets — same "signal reaches the right mounted instance" reasoning
     * as `addTileRequested$`, since the two components communicate through
     * this repository, not a parent/child binding).
     */
    public abstract readonly saveRequested$: Subject<string>;

    /**
     * One-shot signal: "please discard unsaved changes and leave edit mode
     * for THIS Linkview" (Anke, 2026-09-17 — the toolbar's "Abbrechen"
     * button, mirroring `saveRequested$`'s own reasoning).
     */
    public abstract readonly cancelRequested$: Subject<string>;

    /**
     * Creates a board. `icon` is an `id` of the shared icon list
     * (`LINK_TILE_ICONS`, mirrored as `LINKVIEW_ICON_KEYS`); `null` — the
     * create dialog's `ha-input-icon` allows deselecting — an unknown key or
     * no argument at all is stored as the default (`link`), never as-is, so
     * the sidebar always has a glyph rule to hit.
     */
    public abstract create(title: string, icon?: string | null): Promise<Linkview>;

    /**
     * Renames a board (Anke, 2026-09-19: the sidebar row's 3-dot menu). The icon
     * is deliberately not changeable here. Resolves with the renamed board and
     * has already replaced it in `items$` by then.
     *
     * REJECTS when the change did not happen — a blank title, an unknown board,
     * or a server that refused (a board locked by somebody else answers HTTP
     * 409, surfaced as the `LockConflictError`/`HttpError` the command bus
     * carried). Callers must show that failure: a resolved promise is the only
     * signal that the new title is real.
     */
    public abstract rename(uid: string, title: string): Promise<Linkview>;

    /**
     * Deletes a board. Its TILES SURVIVE: the backend handler deliberately does
     * not touch them (a backend test pins that down), so neither this contract
     * nor any confirmation text may tell the user the tiles are lost.
     *
     * Resolves once the board is gone from `items$`; REJECTS, leaving the list
     * untouched, when the server refused (e.g. the board is locked by somebody
     * else) — see `rename` for why callers must surface that.
     */
    public abstract delete(uid: string): Promise<void>;

    /**
     * Takes the edit lock on a board, so this user may drag its tiles.
     *
     * MIND THE POLARITY: this is the successor of the old `setLocked(uid,
     * false)` — `locked === true` means VIEW mode, so setting it to false was
     * the call that made a board EDITABLE. Reading the old boolean as "locked =
     * somebody has the lock" and mapping `setLocked(uid, true)` to `acquireLock`
     * inverts every call site at once.
     *
     * Rejects with a `LockConflictError` when somebody else holds it (the
     * backend answers HTTP 409 and nothing was written); with a plain `Error` on
     * any other failure. That distinction is what lets the caller warn and offer
     * a take-over rather than showing a generic "could not save".
     */
    public abstract acquireLock(uid: string): Promise<void>;

    /** Gives the lock back. Releasing a board this user does not hold is refused
     *  by the backend — taking over is `overrideLock`, and that path carries the
     *  warning; an unguarded release would be a side door around it. */
    public abstract releaseLock(uid: string): Promise<void>;

    /**
     * Takes the lock over from somebody else, after the user has been warned and
     * the holder named.
     *
     * `expectedHolderUid` IS THE COMPARE-AND-SET: the holder the client saw when
     * it rendered its warning, passed back VERBATIM in the transport spelling it
     * was read in. The server refuses the take-over when the row has moved on
     * since. Without it, two people who both saw the same warning both take
     * over, the last writer wins, and the first is silently displaced having
     * been told they succeeded.
     *
     * `null` is a legitimate value, not a missing one: it means "the client
     * believed the board was FREE". That is the same compare, and it is refused
     * if somebody acquired the lock in the race window.
     */
    public abstract overrideLock(uid: string, expectedHolderUid: string | null): Promise<void>;

    public abstract requestAddTile(uid: string): void;

    public abstract requestSave(uid: string): void;

    public abstract requestCancel(uid: string): void;

    public abstract getTiles(linkviewUid: string | undefined): Promise<LinkTile[]>;

    /**
     * Persists; resolves to `false` instead of throwing when that fails
     * (e.g. localStorage quota) — the caller decides, impl-agnostically, how
     * to surface that (see review 0025-01/02 `UiHelper.notify`).
     *
     * Callers may invoke this MULTIPLE TIMES concurrently without awaiting
     * the previous call (`dashboard-creator`'s Gridstack `'change'` handler
     * fires this way on purpose — see its own doc comment) — this contract
     * does NOT guarantee call order is preserved in resolution order. Known,
     * accepted limitation (review Code-Lauf 01, WARNING): irrelevant for
     * `LocalStorageLinkviewRepository` (synchronous I/O, resolves in
     * submission order regardless), but a future backend implementation
     * whose requests can complete out of order would need its own
     * serialization/coalescing (e.g. a `switchMap`/`exhaustMap` queue) to
     * keep this guarantee-free contract from becoming a real "last response
     * wins, not last write wins" bug.
     */
    public abstract saveTiles(linkviewUid: string | undefined, tiles: LinkTile[]): Promise<boolean>;

}

/**
 * tsyringe DI token for `AbstractLinkviewRepository`. A `Symbol`, NOT the
 * abstract class itself — tsyringe's `InjectionToken<T>` type
 * (`constructor<T> | string | symbol | DelayedConstructor<T>`) does not
 * accept an abstract class (`registerSingleton(AbstractLinkviewRepository,
 * ...)` is a TS2345 `vite build`/esbuild would silently miss, being
 * typecheck-free). Mirrors the existing string-token pattern in
 * `bootstrap.ts` (`"UserRepository"`/`"DynamicLoadService"`), Symbol instead
 * of string to avoid any accidental name collision.
 */
export const LINKVIEW_REPOSITORY: InjectionToken<AbstractLinkviewRepository> =
    Symbol('AbstractLinkviewRepository');
